import React, { useEffect, useMemo, useState } from "react";
import {
  FiGrid,
  FiMic,
  FiMicOff,
  FiPause,
  FiEdit3,
  FiPhoneOff,
  FiPlay,
  FiUser,
} from "react-icons/fi";
import useCallCenterT from "../../../hooks/useCallCenterT";

const formatElapsed = (totalSeconds) => {
  const seconds = Math.max(0, Number(totalSeconds) || 0);
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  const mm = String(mins).padStart(2, "0");
  const ss = String(secs).padStart(2, "0");
  if (hrs > 0) {
    return `${String(hrs).padStart(2, "0")}:${mm}:${ss}`;
  }
  return `${mm}:${ss}`;
};

const ActionChip = ({ active, danger, onClick, label, children, disabled }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    aria-pressed={active}
    aria-label={label}
    className={`inline-flex h-9 shrink-0 items-center gap-2 rounded-lg px-3 text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-40 ${
      danger
        ? "bg-rose-600 text-white hover:bg-rose-500"
        : active
          ? "bg-white/20 text-white"
          : "bg-white/10 text-white/90 hover:bg-white/15"
    }`}
  >
    {children}
    <span className="whitespace-nowrap">{label}</span>
  </button>
);

const ActiveCallBar = ({
  activeCall,
  hold = false,
  muted = false,
  connecting = false,
  onMuteChange,
  onHoldChange,
  onHangup,
  onToggleKeypad,
  onContacts,
  onNote,
  keypadOpen = false,
  extensionLabel,
  variant = "panel",
}) => {
  const t = useCallCenterT();
  const [elapsedSec, setElapsedSec] = useState(0);

  const startedAtMs = useMemo(() => {
    if (activeCall?.startedAt) {
      const parsed = Date.parse(activeCall.startedAt);
      if (!Number.isNaN(parsed)) return parsed;
    }
    return Date.now();
  }, [activeCall?.startedAt, activeCall?.phoneNumber, activeCall?.callLogId]);

  useEffect(() => {
    const tick = () => {
      setElapsedSec(Math.max(0, Math.floor((Date.now() - startedAtMs) / 1000)));
    };
    tick();
    const timer = window.setInterval(tick, 1000);
    return () => window.clearInterval(timer);
  }, [startedAtMs]);

  if (!activeCall) return null;

  const phoneNumber = activeCall.phoneNumber || t("ccUnknownCaller");
  const displayName = activeCall.displayName || activeCall.callerName || phoneNumber;
  const isOutbound = String(activeCall.direction || "").toUpperCase() === "OUTBOUND";
  const statusLabel = connecting
    ? t("ccGlobalDialerConnecting")
    : hold
      ? t("ccCallOnHold")
      : isOutbound
        ? t("ccActiveOutboundCall")
        : t("ccActiveInboundCall");
  const badge = extensionLabel || activeCall.extension || "EEMAD";
  const showControls = typeof onHangup === "function";

  const bar = (
    <div
      className={`flex w-full flex-wrap items-center gap-3 rounded-2xl bg-[#0f2744] px-4 py-3 text-white shadow-[0_10px_30px_rgba(15,39,68,0.35)] ${
        variant === "dock" ? "rounded-xl" : ""
      }`}
    >
      <div className="flex min-w-0 flex-1 items-center gap-4">
        <p className="shrink-0 font-mono text-lg font-semibold tabular-nums tracking-wide text-white">
          {formatElapsed(elapsedSec)}
        </p>
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-white">{displayName}</p>
          <p className="mt-0.5 flex items-center gap-1.5 text-xs text-slate-300">
            <span
              className={`h-1.5 w-1.5 shrink-0 rounded-full ${
                hold ? "bg-amber-400" : connecting ? "bg-sky-400" : "bg-emerald-400"
              }`}
            />
            <span className="truncate">{statusLabel}</span>
          </p>
        </div>
      </div>

      {showControls && (
        <div className="flex flex-wrap items-center gap-2">
          <ActionChip
            active={hold}
            onClick={() => onHoldChange?.(!hold)}
            label={hold ? t("ccCallResume") : t("ccCallHold")}
          >
            {hold ? (
              <FiPlay className="h-4 w-4" strokeWidth={2.25} />
            ) : (
              <FiPause className="h-4 w-4" strokeWidth={2.25} />
            )}
          </ActionChip>

          <ActionChip
            active={muted}
            onClick={() => onMuteChange?.(!muted)}
            label={muted ? t("ccCallUnmute") : t("ccCallMute")}
          >
            {muted ? (
              <FiMicOff className="h-4 w-4" strokeWidth={2.25} />
            ) : (
              <FiMic className="h-4 w-4" strokeWidth={2.25} />
            )}
          </ActionChip>

          {typeof onToggleKeypad === "function" && (
            <ActionChip
              active={keypadOpen}
              onClick={onToggleKeypad}
              label={t("ccCallKeypad")}
            >
              <FiGrid className="h-4 w-4" strokeWidth={2.25} />
            </ActionChip>
          )}

          {typeof onContacts === "function" && (
            <ActionChip onClick={onContacts} label={t("ccCallContacts")}>
              <FiUser className="h-4 w-4" strokeWidth={2.25} />
            </ActionChip>
          )}

          {typeof onNote === "function" && (
            <ActionChip onClick={onNote} label={t("ccCallNote")}>
              <FiEdit3 className="h-4 w-4" strokeWidth={2.25} />
            </ActionChip>
          )}

          <ActionChip danger onClick={onHangup} label={t("ccCallEndCall")}>
            <FiPhoneOff className="h-4 w-4" strokeWidth={2.25} />
          </ActionChip>

          <span className="inline-flex h-8 items-center rounded-md bg-slate-700/80 px-2.5 text-[11px] font-semibold tracking-wide text-slate-200">
            {badge}
          </span>
        </div>
      )}
    </div>
  );

  if (variant === "dock") {
    return (
      <div className="pointer-events-none fixed inset-x-0 bottom-0 z-[9999] flex justify-center px-3 pb-3 sm:px-4 sm:pb-4">
        <div className="pointer-events-auto w-full max-w-6xl">{bar}</div>
      </div>
    );
  }

  return bar;
};

export default ActiveCallBar;
